import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot,
} from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TransferStateService } from '@scullyio/ng-lib';

@Injectable({
  providedIn: 'root',
})
export class ProducteResolver implements Resolve<any> {
  constructor(
    private http: HttpClient,
    private transferStateService: TransferStateService
  ) {}

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> {
    const lang = route.url[0].path;

    const mainDataUrl = `../../../assets/${environment.projectName}/pages-components.json`;
    const metadataUrl = `../../../assets/${environment.projectName}/content/${lang}/product-page/meta-data.json`;

    // Fetch both main data and metadata
    return forkJoin({
      mainData: this.transferStateService.useScullyTransferState(
        'maindata',
        this.http.get<any>(mainDataUrl).pipe(catchError(() => of(null)))
      ),
      metadata: this.transferStateService.useScullyTransferState(
        'metadata',
        this.http.get<any>(metadataUrl).pipe(catchError(() => of(null)))
      ),
    }).pipe(
      switchMap(({ mainData, metadata }) => {
        const productComponents = mainData?.productComponents || [];

        if (productComponents.length > 0) {
          // For each component in serviceComponents, fetch the corresponding JSON file
          const componentWithDetailsRequests = productComponents.map(
            (item: any) => {
              const fileName = item.fileName;
              const subJsonPath = `../../../assets/${environment.projectName}/content/${lang}/product-page/${fileName}.json`;

              // Fetch the sub-component JSON and add it as `details` to the item
              return this.transferStateService.useScullyTransferState(
                'subJson',
                this.http.get(subJsonPath).pipe(
                  map((details) => ({ ...item, details })), // Add the fetched JSON as `details`
                  catchError((error) => {
                    console.error(`Failed to fetch ${fileName}:`, error);
                    return of({ ...item, details: null }); // Return the item with `details` as null on error
                  })
                )
              );
            }
          );

          // Use forkJoin to wait for all requests to complete and return the enriched serviceComponents
          return forkJoin(componentWithDetailsRequests).pipe(
            map((componentsWithDetails) => {
              // Combine the enriched serviceComponents with metadata
              return {
                ...mainData,
                productComponents: componentsWithDetails,
                metadata,
              };
            })
          );
        } else {
          console.warn('No components found in serviceComponents.');
          return of({ ...mainData, metadata });
        }
      }),
      catchError((error) => {
        console.error('Error fetching data:', error);
        return of(null); // Handle any errors in fetching data
      })
    );
  }
}
